import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { Money, moneyNode } from "@/components/Money";
import { Card, OutstandingCard } from "@/components/dashboard/ErpWidgets";
import { FilterBar } from "@/components/filters/FilterBar";
import { TileBadge } from "@/components/filters/TileBadge";
import { useSource } from "@/context/SourceContext";
import { apiError } from "@/lib/api";
import { dayLabel, useFilters } from "@/lib/filters";
import { monthLabel } from "@/lib/format";
import { QUICK_RANGES } from "@/lib/ranges";

const kfmt = (v) => (Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(0)}k` : `${v}`);
const LAST_12 = QUICK_RANGES[3].range();
const ALL_HISTORY = ["2000-01-01", new Date().toISOString().slice(0, 10)];

const useFilteredData = (path) => {
  const src = useSource();
  const fl = useFilters(useMemo(() => (src.bounds ? src.bounds : LAST_12), [src.bounds]));
  const [data, setData] = useState(null);
  useEffect(() => {
    src.get(`${path}?${fl.query({ granularity: "monthly" })}`).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  }, [src, path, fl.key]); // eslint-disable-line react-hooks/exhaustive-deps
  const period = fl.dated ? `${dayLabel(fl.range[0])} – ${dayLabel(fl.range[1])}` : src.bounds ? "Permitted range" : "Last 12 months";
  return { fl, data, period, src };
};

const Bars = ({ data, dataKey, name, testid }) => (
  <Card title="By month" testid={testid}>
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data}>
          <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748B" }} tickLine={false} /><YAxis tickFormatter={kfmt} tick={{ fontSize: 11, fill: "#64748B" }} tickLine={false} axisLine={false} width={44} />
          <Tooltip formatter={(v) => moneyNode(v)} /><Bar dataKey={dataKey} name={name} fill="#0D9488" isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  </Card>
);

const Note = ({ text }) => text ? <p className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900" data-testid="excluded-note">{text}</p> : null;

export const OutstandingPage = () => (
  <div className="max-w-3xl space-y-6" data-testid="outstanding-page">
    <PageHeader brand eyebrow="Receivables" title="Outstanding" subtitle="Customer receivables snapshot · not part of P&L" />
    <OutstandingCard />
  </div>
);

export const ExpensesPage = () => {
  const { fl, data, period, src } = useFilteredData("/sections/expenses");
  return (
    <div className="space-y-6" data-testid="expenses-page">
      <PageHeader brand eyebrow="Expenses" title="Expenses" subtitle={`${period} · all expense report records · cost of sales by FS group, other lines per the expense mapping`} />
      <FilterBar fl={fl} periodLabel={period} allHistory={src.bounds ? null : ALL_HISTORY} />
      <Note text={src.note} />
      {data && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {[["total_expenses", "Total expenses"], ["cos_expenses", "Cost of sales expenses"], ["opex", "Operating expenses"], ["other_charges", "Depreciation and finance cost"]].map(([k, l]) => (
              <div key={k} className="rise-in rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                <div className="text-xs text-slate-500">{l}</div><Money value={data.totals[k]} className="mt-1 block text-xl font-semibold" data-testid={`expenses-total-${k}`} />
                {k === "total_expenses" && <div className="mt-2"><TileBadge status={data.status} testid="expenses-badge" /></div>}
              </div>
            ))}
          </div>
          <Card title="Expense lines" subtitle="Share of total expenses" testid="expense-lines">
            <table className="w-full text-sm">
              <tbody>
                {data.lines.map((r) => (
                  <tr key={r.key} className="border-b border-slate-100" data-testid={`expense-line-${r.key}`}>
                    <td className="py-2 text-slate-800">{src.readOnly ? r.label : <Link to={`/lines/${r.key}`} className="hover:text-teal-700 hover:underline">{r.label}</Link>} <TileBadge status={data.line_status?.[r.key]} testid={`expense-badge-${r.key}`} /></td>
                    <td className="text-xs text-slate-500">{r.section === "other" ? "Depreciation and finance cost" : "Operating expenses"}</td>
                    <td className="text-right"><Money value={r.amount} /></td><td className="w-20 text-right text-xs text-slate-500">{r.share_pct == null ? "—" : `${r.share_pct.toFixed(1)}%`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
          <Bars data={data.periods.map((p) => ({ label: monthLabel(p.period, true), v: p.total_expenses }))} dataKey="v" name="Total expenses" testid="expenses-chart" />
        </>
      )}
    </div>
  );
};

export const CostOfSalesPage = () => {
  const { fl, data, period, src } = useFilteredData("/sections/cogs");
  return (
    <div className="space-y-6" data-testid="cogs-page">
      <PageHeader brand eyebrow="P&L" title="Cost of goods sold" subtitle={`${period} · sales report Cost column (returns netted)`} />
      <FilterBar fl={fl} periodLabel={period} allHistory={src.bounds ? null : ALL_HISTORY} />
      {data && (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rise-in rounded-lg border border-slate-900 bg-slate-900 p-5 text-white shadow-sm"><div className="text-xs text-slate-300">Cost of goods sold</div><Money value={data.total} className="mt-1 block text-xl font-semibold" data-testid="cogs-total" /><div className="mt-2"><TileBadge status={data.status} testid="cogs-badge" /></div></div>
            <div className="rise-in rounded-lg border border-slate-200 bg-white p-5 shadow-sm"><div className="text-xs text-slate-500">Revenue (net sales)</div><Money value={data.revenue} className="mt-1 block text-xl font-semibold" data-testid="cogs-revenue" /></div>
          </div>
          <Bars data={data.periods.map((p) => ({ label: monthLabel(p.period, true), v: p.total }))} dataKey="v" name="Cost of goods sold" testid="cogs-chart" />
          <div className="grid gap-4 lg:grid-cols-2">
            {[["by_item", "By item", "Item"], ["by_customer", "By customer", "Customer"]].map(([k, title, col]) => (
              <Card key={k} title={title} subtitle="Top 25 by sales report Cost" testid={`cogs-${k}`}>
                <div className="max-h-[420px] overflow-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-white text-left text-xs uppercase tracking-wide text-slate-500"><tr><th className="py-2">{col}</th><th className="text-right">Sales</th><th className="text-right">Cost</th></tr></thead>
                    <tbody>
                      {(data[k] || []).length === 0 && <tr><td colSpan={3} className="py-6 text-center text-slate-500" data-testid={`cogs-${k}-empty`}>No sales in this period</td></tr>}
                      {(data[k] || []).map((r) => <tr key={r.name} className="border-t border-slate-100" data-testid={`cogs-${k}-row`}><td className="py-2">{r.name}</td><td className="text-right"><Money value={r.sales} /></td><td className="text-right"><Money value={r.cost} /></td></tr>)}
                    </tbody>
                  </table>
                </div>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
